import { DOMAIN, LoggerFactory } from 'Services/Logger';
import { TopBannerType } from 'Utils/parseHomeBehavior';
import { useCallback, useEffect, useState } from 'react';

const logger = LoggerFactory.getLogger(DOMAIN.CUSTOMER_RELATIONS);

type Nullable<T> = T | null;

export function useStorage<Type>(storage: Storage, key: string, initialValue: Type) {
  const [storedValue, setStoredValue] = useState<Type>(() => {
    try {
      const item = storage.getItem(key);
      return item !== null ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  const setValue = useCallback((
    newValue: Nullable<Type> | ((oldValue: Nullable<Type>) => Nullable<Type>),
  ) => {
    try {
      const valueToStore = newValue instanceof Function ? newValue(storedValue) : newValue;

      const event = new StorageEvent('storage', {
        bubbles: true,
        key,
        newValue: JSON.stringify(valueToStore),
      });

      window.dispatchEvent(event);
    } catch (error) {
      logger.error('Set value to storage', '', { error });
    }
  }, [key]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== key) return;
      if (e.newValue === null || e.newValue === 'null') {
        storage.removeItem(key);
        return;
      }

      setStoredValue(JSON.parse(e.newValue));
      storage.setItem(key, e.newValue);
    };

    window.addEventListener('storage', handler);

    return () => window.removeEventListener('storage', handler);
  }, [key]);

  return [storedValue, setValue] as const;
}

export function useLocalStorage<Type>(key: string, initialValue: Type) {
  return useStorage(window.localStorage, key, initialValue);
}

export function useSessionStorage<Type>(key: string, initialValue: Type) {
  return useStorage(window.sessionStorage, key, initialValue);
}

export function useSkipStorePaymentModal() {
  return useLocalStorage('skipStorePaymentModal', false);
}

export function useShowTestingEnvBanner() {
  return useSessionStorage('SHOW_TESTING_ENV_BANNER', true);
}

export function useHasOferringBenefits() {
  return useLocalStorage('HAS_BENEFITS', false);
}

export type ClosedNotifications = Partial<Record<TopBannerType, boolean>>;
export function useHomeClosedNotifications() {
  return useSessionStorage<ClosedNotifications>('HOME_CLOSED_NOTIFICATIONS', {});
}
